import dayjs from "dayjs";
import styled, { css } from "styled-components";

const Chip = styled.span<{ $small: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--brand-blue-25);
  border: 1px solid var(--brand-purple-500);
  color: var(--brand-purple-700);
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
  vertical-align: middle;

  /* The header's user menu (John's review): the same badge, 18px tall instead of 23px, so a third line
     under the role keeps the trigger well inside the 80px header. */
  ${({ $small }) =>
    $small &&
    css`
      gap: 3px;
      padding: 0 6px;
      font-size: 11px;
      line-height: 16px;

      .pi {
        font-size: 11px;
      }
    `}
`;

interface ICertifiedChipProps {
  /** ISO time the user was certified; nothing renders without it. */
  certifiedAt?: string | null;
  /** "small" is the header's compact chip; the default is the profile page's. */
  size?: "default" | "small";
}

/** "Certified Oct 21, 2026" — the hover text, in the viewer's local time. */
export function formatCertifiedDate(certifiedAt: string): string {
  return dayjs(certifiedAt).format("MMM D, YYYY");
}

/** True when `certifiedAt` is a date the chip can show. */
export function isCertifiedDate(
  certifiedAt: string | null | undefined
): certifiedAt is string {
  return !!certifiedAt && dayjs(certifiedAt).isValid();
}

/** "Clear Certified, certified Oct 21, 2026" — what a screen reader hears for the badge. */
export function certifiedAriaLabel(certifiedAt: string): string {
  return `Clear Certified, certified ${formatCertifiedDate(certifiedAt)}`;
}

/** CLE-3015 — the "Clear Certified" badge, with the certification date on hover. */
export function CertifiedChip({
  certifiedAt,
  size = "default",
}: ICertifiedChipProps) {
  if (!isCertifiedDate(certifiedAt)) {
    return null;
  }

  return (
    <Chip
      $small={size === "small"}
      title={`Certified ${formatCertifiedDate(certifiedAt)}`}
      aria-label={certifiedAriaLabel(certifiedAt)}
    >
      <i className="pi pi-verified" aria-hidden />
      Clear Certified
    </Chip>
  );
}
